<template>
  <el-select
    ref="select"
    v-bind="$attrs"
    v-on="$listeners"
    :value="selectValue"
    :size="mapSize"
    :style="{ width: mapWidth }"
    :multiple="type === 'multi-check' || multiple"
    :collapse-tags="type === 'multi-check' || collapseTags"
    :class="{
      'multi-check': type === 'multi-check',
      'gj-select--font14': font14,
      'gj-empty--tags':
        (type === 'multi-check' || collapseTags) &&
        (isMeaningless(selectValue) || selectValue.length === 0),
    }"
    :popper-class="mapPopperClass"
    :clearable="clearable"
    :filterable="filterable"
    class="gj-select"
    @visible-change="visibleChange($event)"
    @input="onInput($event)"
  >
    <template v-if="type === 'multi-check'">
      <el-option-group class="search-group" key="search-group">
        <el-option value="-1">
          <div class="multi-check--input" @click.stop>
            <gj-input
              ref="searchInput"
              v-model="searchText"
              placeholder="搜索"
              underline
              multiCheckSearch
              width="100%"
              @input="refreshCheckShow"
            ></gj-input>
          </div>
        </el-option>
      </el-option-group>
      <el-option-group class="select-group" key="select-group">
        <template v-if="searchCheckMatched">
          <el-option value="-2" v-if="isShowAllChecked">
            <div class="multi-checkbox" @click.stop>
              <el-checkbox
                :value="allCheck"
                :indeterminate="allCheckIndeterminate"
                class="select-checkbox"
                @change="changeAllCheck($event)"
              >
                <span class="font12">全选</span>
              </el-checkbox>
            </div>
          </el-option>
          <template v-for="item in checkOptions">
            <el-option
              v-show="item.show"
              :key="item.id"
              :label="item.text"
              :value="item.id"
            >
              <div class="multi-checkbox" @click.stop>
                <el-checkbox
                  :label="item.id"
                  :disabled="item.disabled"
                  v-model="item.checked"
                  class="select-checkbox"
                  @change="changeCheck(item)"
                >
                  <template v-if="$scopedSlots.content">
                    <slot name="content" :item="item"></slot>
                  </template>
                  <span v-else class="font12">{{ item.text }}</span>
                  <!--                  注意：当用作于店铺下拉框时，用于展示店铺停用状态-->
                  <span v-if="item.storeStatus === 'Inactive'" class="country"
                    >停用</span
                  >
                </el-checkbox>
              </div>
            </el-option>
          </template>
        </template>
        <template v-else>
          <div class="dropdown__empty">无匹配数据</div>
        </template>
      </el-option-group>
    </template>
    <template v-else-if="type === 'tree-select' || type === 'custom'">
      <slot></slot>
    </template>
    <template v-else-if="type === 'add-select'">
      <slot></slot>
    </template>
    <template v-else>
      <el-tooltip
        v-for="item in options"
        :key="item.id"
        :disabled="toolTipOptions(item).disabled"
        effect="dark"
        :content="toolTipOptions(item).content"
        placement="top-start"
      >
        <el-option
          :label="item.text"
          :value="item.id"
          :disabled="item.disabled"
        >
          <slot :option="item"></slot>
        </el-option>
      </el-tooltip>
    </template>
  </el-select>
</template>

<script>
import { sizeMap } from '@/components/common';
import GjInput from '@/components/input/gj-input.vue';
import { isMeaningful, isMeaningless, transformToMap } from '@/utils/common';
import { font14MixIn, isMeaninglessMixin } from '@/minxins';

export default {
  name: 'select-ad-aotu',
  mixins: [font14MixIn, isMeaninglessMixin],
  components: { GjInput },
  props: {
    value: {
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    width: {
      type: String,
      default: '100px',
    },
    size: {
      type: String,
      default: 'default',
      validator: function (value) {
        return ['default', 'mini'].indexOf(value) !== -1;
      },
    },
    type: {
      type: String,
      default: 'default',
      validator: function (value) {
        return (
          [
            'default',
            'multi-check',
            'tree-select',
            'custom',
            'add-select',
          ].indexOf(value) !== -1
        );
      },
    },
    popperClass: {
      type: String,
    },
    popoverWidth: {
      type: String,
      default: '180px',
    },
    multiple: Boolean,
    clearable: {
      type: Boolean,
      default: true,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    collapseTags: {
      type: Boolean,
      default: false,
    },
    searchComparator: {
      type: Function,
      default: (e, searchText) =>
        e.text.toLowerCase().includes(searchText.toLowerCase()),
    },
    showScroll: {
      // 用于解决滚掉头无法鼠标拖动bug
      type: Boolean,
      default: false,
    },
    isShowAllChecked: {
      type: Boolean,
      default: true,
    },
    initValue: {
      type: String,
      default: null,
    },
    notInit: {
      // 解决双向绑定组件多次触发的问题
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectValue:
        this.isMultiCheck || this.multiple ? this.value || [] : this.value,
      checkOptions: [],
      searchCheckMatched: true,
      allCheckIndeterminate: null,
      allCheck: null,
      searchText: '',
      optionMap: {},
      flag: true,
    };
  },
  created() {
    this.refreshAllCheck();
    this.refreshCheckShow();
  },
  computed: {
    mapSize() {
      return sizeMap[this.size];
    },
    mapPopperClass() {
      if (this.isMultiCheck || this.isTreeSelect || this.isCustom) {
        if (this.showScroll) {
          return 'gj-custom-select--popper hide-scroll';
        }
        return 'gj-custom-select--popper';
      }
      return this.popperClass;
    },
    mapWidth() {
      return isMeaningful(this.width) ? this.width : '100px';
    },
    isMultiCheck() {
      return this.type === 'multi-check';
    },
    isTreeSelect() {
      return this.type === 'tree-select';
    },
    isCustom() {
      return this.type === 'custom';
    },
    valueOptions() {
      return {
        value: this.value,
        options: this.options,
      };
    },
  },
  watch: {
    options: {
      immediate: true,
      handler(newValue) {
        this.checkOptions = newValue || [];
        if (this.isMultiCheck) {
          this.refreshCheck();
          this.refreshAllCheck();
          this.refreshCheckShow();
        }
        if (this.$listeners['input-option']) {
          this.optionMap = transformToMap(newValue);
        }
      },
    },
    value: {
      handler: function (value) {
        this.selectValue =
          this.isMultiCheck || this.multiple ? value || [] : value;
      },
      immediate: true,
    },
    valueOptions: {
      handler: function ({ value, options }) {
        // multi-check类型处理
        if (this.isMultiCheck) {
          // 值勾选
          if (isMeaningful(value) && this._.isArray(value)) {
            this.selectValue = value;
            this.refreshCheck();
            this.refreshAllCheck();
            this.refreshCheckShow();
          }
        }
      },
      immediate: true,
    },
    initValue(nVal) {
      if (this.flag && this.notInit) {
        // console.log('回显事件触发');
        this.$emit('change', { initValue: nVal });
        this.flag = false;
      }
    },
  },
  methods: {
    toolTipOptions(instance) {
      return {
        disabled: isMeaningless(instance.tooltip),
        content: instance.tooltip + '',
      };
    },
    onInput(value) {
      this.selectValue = value;
      if (this.isMultiCheck) {
        this.checkOptions.forEach((e) => {
          e.checked = value.some((id) => e.id === id);
        });
        this.refreshAllCheck();
      }
      if (this.$listeners['input-option']) {
        this.$emit('input-option', this.optionMap[this.selectValue] || null);
      }
    },
    changeCheck(item) {
      const index = this.selectValue.findIndex((e) => e === item.id);
      if (index >= 0) {
        this.selectValue.splice(index, 1);
      } else {
        this.selectValue.push(item.id);
      }
      this.refreshAllCheck();
      this.$emit('input', this.selectValue);
      this.$emit('change', this.selectValue);
    },
    changeAllCheck(checked) {
      this.checkOptions
        .filter((e) => e.show)
        .forEach((e) => {
          e.checked = checked;
        });
      this.selectValue = this.checkOptions
        .filter((e) => e.show && e.checked)
        .map((e) => e.id);
      this.refreshAllCheck();
      this.$emit('input', this.selectValue);
      this.$emit('change', this.selectValue);
    },
    refreshCheck() {
      this.checkOptions.forEach((option) => {
        option.checked = (this.value || []).some((id) => option.id === id);
      });
    },
    refreshAllCheck() {
      const checkOptions = this.checkOptions.filter((e) => e.show);
      this.allCheckIndeterminate =
        checkOptions.some((e) => e.checked) &&
        checkOptions.some((e) => !e.checked);
      this.allCheck = checkOptions.every((e) => e.checked);
    },
    refreshCheckShow() {
      this.checkOptions = this.checkOptions.map((e) =>
        Object.assign(e, {
          show: this.searchComparator(e, this.searchText || ''),
        })
      );
      this.refreshAllCheck();
      this.$nextTick(() => {
        this.searchCheckMatched = this.checkOptions.some((e) => e.show);
      });
    },
    visibleChange(visible) {
      if (visible && this.isMultiCheck) {
        this.$nextTick(() => {
          // this.$refs.searchInput.$refs.gjInput.$refs.input.focus();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/variables.scss';
.select-checkbox {
  display: block;
  height: 32px;
  padding: 0 10px;
}
.multi-check {
  // min-width: 160px !important;
}
.multi-check ::v-deep .el-tag.el-tag--info .el-tag__close {
  display: none;
}
.multi-check ::v-deep .el-select__tags-text {
  max-width: 48px;
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: margin 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.search-group {
  padding-bottom: 0;
}
.select-group {
  max-height: 300px;
  margin-top: 4px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 5px !important;
    height: 5px !important;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
  }
}
.multi-check--input {
  // margin-top: 6px;
  padding: 0 10px;
}
.multi-checkbox:hover {
  background-color: #f5f7fa;
}
.gj-select--font14 ::v-deep .el-input__inner {
  font-size: 14px !important;
}
::v-deep .el-select__tags .el-select__input {
  margin-left: 4px;
}
.gj-empty--tags ::v-deep .el-select__tags .el-select__input {
  margin-left: 15px;
}
::v-deep .el-select__tags:hover {
  cursor: text;
}
.dropdown__empty {
  padding: 10px 0;
  text-align: center;
  color: #999;
  font-size: 12px;
}
/deep/ .el-input:hover {
  .el-input__inner {
    border-color: $gj-bule-color;
    //box-shadow: $gj-boxShadow-small;
  }
}
/deep/ .el-input__inner {
  border-radius: 2px;
}
/deep/ .el-tag.el-tag--info {
  border-radius: 2px;
  background: #f6f8fb;
  color: #333333;
}
/deep/ .el-tag.el-tag--mini {
  padding: 0 6px;
  border: none;
}
/deep/ .is-focus {
  .el-input__inner {
    border-color: #1f6eef;
    box-shadow: $gj-boxShadow-small;
  }
}
/deep/ .el-tag.el-tag--info .el-tag__close {
  background: #c0c4cc;
  color: #fff;
}
/deep/ .el-input__inner {
  &::-webkit-input-placeholder {
    color: #999999;
    font-weight: 400;
  }
}
.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover {
  background-color: $gj-hover-color-b1;
}
.country {
  margin-left: 8px;
  color: #bfbfbf;
}
</style>
